"use client";

import React from "react";
import { Button, Spinner, cn } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import { DOCXViewer } from "@webuild-ai/react-ui/components";

import { ModalOverlay, ModalHeader, ModalBody, ModalFooter } from "@/components/UI/ModalOverlay";
import { extractSafeDownloadUrl, isValidOfficeViewerUrl } from "@/utils/urlValidation";
import { ExcelViewer } from "@/components/Citations/viewers/ExcelViewer";
import { PPTXViewer } from "@/components/Citations/viewers/PPTXViewer";
import { PDFViewer } from "@/components/Citations/viewers/PDFViewer";
import { DocumentType, detectDocumentTypeFromMimeAndFilename } from "@/utils/fileUtils";
import { logger } from "@/telemetry";

export type FilePreviewModalProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  fileName?: string;
  fileType?: string;
  fileUrl?: string | null;
  /** Document data as ArrayBuffer for client-side Office rendering (DOCX, XLSX, PPTX) */
  documentData?: ArrayBuffer | null;
  isLoading?: boolean;
  error?: string | null;
  onDownload?: () => void | Promise<void>;
};

export const FilePreviewModal: React.FC<FilePreviewModalProps> = ({
  isOpen,
  onOpenChange,
  fileName,
  fileType,
  fileUrl,
  documentData,
  isLoading,
  error,
  onDownload,
}) => {
  const { t } = useTranslation();
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [toast, setToast] = React.useState<{
    visible: boolean;
    type: "loading" | "success" | "error";
    message: string;
  }>({ visible: false, type: "loading", message: "" });

  const canEmbed = React.useMemo(() => {
    if (!fileType) return true; // try embed by default
    const lower = fileType.toLowerCase();

    return (
      lower.includes("pdf") ||
      lower.startsWith("image/") ||
      lower === "text/plain" ||
      lower === "text/markdown" ||
      lower === "text/html"
    );
  }, [fileType]);

  const isImage = React.useMemo(() => {
    const type = (fileType || "").toLowerCase();

    if (type.startsWith("image/")) return true;
    if (!type && fileUrl) {
      // fallback by extension if type is unknown
      return /(\.(png|jpe?g|gif|webp|svg))(\?|#|$)/i.test(fileUrl);
    }

    return false;
  }, [fileType, fileUrl]);

  // Detect document types for client-side rendering (Office + PDF)
  // Uses centralised utility for consistent detection across the application
  const documentType = React.useMemo((): DocumentType | null => {
    return detectDocumentTypeFromMimeAndFilename(fileType, fileName);
  }, [fileType, fileName]);

  // Passed through unmodified for the embeddable non-document types below.
  // NOTE: this no longer carries PDFs. `documentType === "pdf"` intercepts every
  // PDF above and renders it through the client-side PDFViewer, so the browser's
  // native PDF toolbar (and with it zoom in and zoom out) is not reachable here.
  // See the pdfjs-dist section of CLAUDE.md before attributing a PDF sizing or
  // zoom report to a dependency upgrade.
  const embedUrl = React.useMemo(() => {
    return fileUrl;
  }, [fileUrl]);

  const downloadHref = React.useMemo(() => {
    return extractSafeDownloadUrl(fileUrl);
  }, [fileUrl]);

  // toast helpers
  const toastTimerRef = React.useRef<number | null>(null);
  const showToast = React.useCallback(
    (type: "loading" | "success" | "error", message: string, autoHideMs?: number) => {
      setToast({ visible: true, type, message });
      if (toastTimerRef.current) {
        window.clearTimeout(toastTimerRef.current);
        toastTimerRef.current = null;
      }
      if (autoHideMs) {
        toastTimerRef.current = window.setTimeout(() => {
          setToast((prev) => ({ ...prev, visible: false }));
          toastTimerRef.current = null;
        }, autoHideMs);
      }
    },
    [],
  );

  const handleDownloadPress = React.useCallback(async () => {
    if (!onDownload || isDownloading) return;
    try {
      setIsDownloading(true);
      showToast("loading", t("Downloading...") || "Downloading...");
      await onDownload();
      showToast("success", t("Download complete") || "Download complete", 2000);
    } catch (e) {
      logger.error("Download error", e);
      showToast("error", t("Download failed") || "Download failed", 3000);
    } finally {
      setIsDownloading(false);
    }
  }, [onDownload, isDownloading, showToast, t]);

  React.useEffect(() => {
    if (!isOpen) {
      setIsDownloading(false);
      setToast({ visible: false, type: "loading", message: "" });
      if (toastTimerRef.current) {
        window.clearTimeout(toastTimerRef.current);
        toastTimerRef.current = null;
      }
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <ModalOverlay isOpen={isOpen} scrollBehavior="inside" size="4xl" onOpenChange={onOpenChange}>
      <ModalHeader className="flex items-center gap-2 border-b border-divider">
        <Icon className="text-brand-primary" icon="solar:document-linear" width={20} />
        <span className="truncate" title={fileName}>
          {fileName || t("Preview")}
        </span>
      </ModalHeader>
      <ModalBody className="p-0 flex-1 min-h-0 overflow-hidden bg-content1">
        {isLoading ? (
          <div className="flex h-full flex-col items-center justify-center gap-3" role="status">
            <Spinner />
            <p className="text-sm text-default-500">{t("Loading preview...")}</p>
          </div>
        ) : error ? (
          <div className="p-4 text-danger">{error}</div>
        ) : documentData && documentType ? (
          // Client-side document rendering (PDF + Office).
          // Each viewer owns its own scrollable container (h-[75vh] overflow-auto);
          // the outer ModalBody is overflow-hidden so we never stack scrollbars.
          documentType === "pdf" ? (
            <PDFViewer documentData={documentData} />
          ) : documentType === "docx" ? (
            <DOCXViewer
              documentData={documentData}
              onError={(err) => logger.error("Failed to render DOCX", err)}
            />
          ) : documentType === "excel" ? (
            <ExcelViewer documentData={documentData} filename={fileName} />
          ) : documentType === "pptx" ? (
            <PPTXViewer documentData={documentData} />
          ) : (
            <div className="p-4 text-sm text-default-600">{t("filePreview.unsupportedType")}</div>
          )
        ) : fileUrl ? (
          isImage ? (
            <div className="w-full h-full flex items-center justify-center overflow-auto">
              <img
                alt={fileName || "image-preview"}
                className="max-h-full max-w-full object-contain"
                src={fileUrl}
                style={{ display: "block" }}
              />
            </div>
          ) : isValidOfficeViewerUrl(fileUrl) ? (
            <iframe
              className="w-full h-full"
              sandbox="allow-same-origin allow-scripts allow-popups"
              src={fileUrl}
              style={{ border: "none" }}
              title={fileName || "file-preview"}
            />
          ) : canEmbed ? (
            // Sandbox added for defence-in-depth. Reached for embeddable file types
            // (PDF, text/plain, text/markdown, text/html) and unknown MIME types.
            // Images are handled above via <img>. allow-same-origin needed for PDF.js
            // rendering; allow-scripts needed for interactive content. See PWI-194.
            <iframe
              className="w-full h-full"
              sandbox="allow-same-origin allow-scripts"
              src={embedUrl || fileUrl}
              style={{ border: "none" }}
              title={fileName || "file-preview"}
            />
          ) : (
            <div className="p-4 text-sm text-default-600">
              This file type cannot be previewed here.
            </div>
          )
        ) : (
          <div className="p-4 text-sm text-default-600">No preview available.</div>
        )}
      </ModalBody>
      <ModalFooter className="justify-end gap-3">
        {toast.visible && (
          <div
            className={cn(
              "flex items-center gap-2 px-3 py-1.5 rounded-medium text-tiny",
              // Bare `success` and `danger` rather than a numbered stop. The compat
              // layer maps those two families only at 50, 100 and 200, so the 700
              // stops compiled to nothing and both toast texts inherited the ambient
              // colour. `default` does map to 700, so that branch is left alone.
              toast.type === "success"
                ? "bg-success-100 text-success"
                : toast.type === "error"
                  ? "bg-danger-100 text-danger"
                  : "bg-default-100 text-default-700",
            )}
          >
            {toast.type === "loading" ? (
              <Spinner size="sm" />
            ) : (
              <Icon
                icon={
                  toast.type === "success"
                    ? "solar:check-circle-linear"
                    : "solar:danger-circle-linear"
                }
                width={16}
              />
            )}
            <span>{toast.message}</span>
          </div>
        )}
        {onDownload ? (
          <Button
            aria-label={t("Download")}
            className="min-w-30 btn-brand-primary"
            isDisabled={isDownloading}
            size="sm"
            onPress={handleDownloadPress}
          >
            {isDownloading ? (
              <Spinner className="text-background" size="sm" />
            ) : (
              <Icon icon="solar:download-bold" width={18} />
            )}{" "}
            {isDownloading ? t("Downloading...") : t("Download")}
          </Button>
        ) : downloadHref ? (
          <Button
            aria-label={t("Download")}
            as={"a" as any}
            className="min-w-30 btn-brand-primary"
            download={fileName || "download"}
            href={downloadHref}
            size="sm"
          >
            <Icon icon="solar:download-bold" width={18} /> {t("Download")}
          </Button>
        ) : null}
      </ModalFooter>
    </ModalOverlay>
  );
};

export default FilePreviewModal;
